import { cx } from '../atoms/cx.ts'

import './figures.css'

/**
 * 비전 카메라 프레임 — 와이어프레임 `visionFrame()` 1682행.
 *
 * 🔴 **영상 스트림 엔드포인트가 계약에 없다**(README §10-2). 여기 그리는 것은 오버레이 뿐이고
 *    실제 영상은 붙일 곳이 아직 없다 — 백엔드 협의 대상으로 팀장에게 올렸다.
 *
 * 모듈마다 검사 대상이 다르다(원심 = 로터 슬롯 4 · 어셈블러 = 틸트 스테이지·TC · 염색기 = 셰이커 3×3 · §16-4).
 */
export interface VisionCellView {
  /** 판정 박스 라벨(칸 이름) */
  label: string
  verdict: 'pass' | 'fail'
}

export interface VisionFrameProps {
  /** 카메라 식별자(`cam-1`). 장치 이름이라 번역하지 않는다 */
  camera: string
  /** 해상도 표기 */
  resolution: string
  /** 촬영 시각 표기. 없으면 아직 찍은 적이 없다 */
  timestamp?: string
  /** 라이브 여부. 꺼져 있으면 판정 박스를 회색으로 */
  live: boolean
  /** 오버레이 종류 */
  overlay: 'cent' | 'asm' | 'stain'
  cells: readonly VisionCellView[]
  liveLabel: string
  label: string
}

const CENT_POS: readonly [number, number][] = [
  [160, 40],
  [240, 100],
  [160, 160],
  [80, 100],
]

export function VisionFrame({
  camera,
  resolution,
  timestamp,
  live,
  overlay,
  cells,
  liveLabel,
  label,
}: VisionFrameProps) {
  const cellClass = (verdict: 'pass' | 'fail'): string =>
    cx(
      'fig-vision__cell',
      !live && 'fig-vision__cell--off',
      live && verdict === 'fail' && 'fig-vision__cell--fail',
    )
  const textClass = cx('fig-vision__label', !live && 'fig-vision__label--off')

  const box = (x: number, y: number, w: number, h: number, cell: VisionCellView) => (
    <g key={`${cell.label}-${String(x)}-${String(y)}`}>
      <rect
        className={cellClass(cell.verdict)}
        height={h}
        rx={4}
        width={w}
        x={x - w / 2}
        y={y - h / 2}
      />
      <text className={textClass} textAnchor="middle" x={x} y={y + 5}>
        {cell.label}
      </text>
    </g>
  )

  return (
    <div className="fig-vision">
      <svg
        aria-label={label}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        viewBox="0 0 320 200"
        width="100%"
      >
        {overlay === 'cent' ? (
          <>
            <circle
              className="fig-vision__art"
              cx={160}
              cy={100}
              fill="none"
              r={78}
              strokeWidth={1.5}
            />
            <path className="fig-vision__guide" d="M10 100h300M160 30v140" />
            {cells.slice(0, 4).map((cell, i) => {
              const pos = CENT_POS[i]
              return pos ? box(pos[0], pos[1], 44, 32, cell) : null
            })}
          </>
        ) : overlay === 'asm' ? (
          <>
            <rect
              className="fig-vision__art"
              fill="none"
              height={108}
              rx={6}
              strokeWidth={1.5}
              width={180}
              x={70}
              y={46}
            />
            <path className="fig-vision__guide" d="M10 100h300" />
            {cells[0] ? box(160, 100, 84, 44, cells[0]) : null}
            {cells[1] ? box(160, 60, 52, 20, cells[1]) : null}
          </>
        ) : (
          <>
            <rect
              className="fig-vision__art"
              fill="none"
              height={132}
              rx={6}
              strokeWidth={1.5}
              width={228}
              x={46}
              y={34}
            />
            {cells
              .slice(0, 9)
              .map((cell, i) => box(94 + (i % 3) * 66, 62 + Math.floor(i / 3) * 38, 58, 30, cell))}
          </>
        )}
        <text className="fig-vision__meta" x={10} y={18}>
          {`${live ? '● ' : '○ '}${liveLabel}  ${camera}  ${resolution}`}
        </text>
        <text className="fig-vision__ts" textAnchor="end" x={310} y={190}>
          {timestamp ?? ''}
        </text>
      </svg>
    </div>
  )
}
